import React from "react";
import { Helmet } from "react-helmet-async";
import {
  BarChart3,
  TrendingUp,
  Shield,
  LayoutDashboard,
  Activity,
  Users,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Recruiter Productivity Software for HR and Staffing Firms | TeamGrid";
const SEO_DESCRIPTION = "Increase placements without burning out recruiters. Track pipeline velocity, workload balance and performance drivers with full visibility.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/hr-staffing-firms";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const HRStaffingFirms = () => {
  const navigate = useNavigate();
  // FAQ data specific to HR & Staffing Firms
  const hrStaffingFAQs = [
    {
      question: "How can TeamGrid help recruiters improve time-to-fill?",
      answer:
        "TeamGrid analyzes recruiter work patterns to highlight effective behaviors that shorten time-to-fill and boost recruiter productivity insights.",
    },
    {
      question: "Does TeamGrid track recruiter activity or focus on outcomes?",
      answer:
        "TeamGrid focuses on outcomes by showing productivity trends and hiring impact, not micromanaging recruiter tasks or typing.",
    },
    {
      question: "Can TeamGrid integrate with HR and ATS tools?",
      answer:
        "Yes, TeamGrid works alongside HR systems and ATS by enhancing staffing productivity reporting without replacing core HR software.",
    },
  ];

  // Key Productivity Areas – 4 cards per Figma
  const productivityCards = [
    {
      title: "Recruiter Performance Drivers",
      description:
        "Busy calendars don't always mean strong placement outcomes.",
      points: [
        "Identifies work patterns linked to successful placements",
        "Benchmarks top recruiter behaviors",
        "Distinguishes deep sourcing from admin noise",
        "Replicates high-performer workflows",
      ],
    },
    {
      title: "Pipeline Velocity & Bottlenecks",
      description:
        "Candidates stall for reasons traditional dashboards miss.",
      points: [
        "Tracks time spent in each pipeline stage",
        "Identifies delays caused by clients or scheduling",
        "Highlights drop-off points and causes",
        "Improves flow without sacrificing quality",
      ],
    },
    {
      title: "Sustainable Recruiter Workloads",
      description:
        "Overwork reduces placement success and increases churn.",
      points: [
        "Monitors workload sustainability (not just hours)",
        "Flags burnout risk early",
        "Balances recruiter client loads",
        "Protects recovery time after placement pushes",
      ],
    },
    {
      title: "Client Delivery & Quality",
      description:
        "Clients demand speed but also strong candidate quality.",
      points: [
        "Tracks time-to-fill and responsiveness",
        "Measures submission-to-interview conversion",
        "Monitors offer acceptance effectiveness",
        "Improves client satisfaction through visibility",
      ],
    },
  ];

  const productivityCardAccentColors = ["#00D1C1", "#366EB6", "#6C59A6", "#F2673A"];

  // Complete Feature Suite – 6 features per Figma
  const customFeatures = [
    {
      icon: BarChart3,
      iconColor: "text-[#00D1C1]",
      title: "Recruiter Performance Intelligence",
      desc: "Identify what drives placements",
    },
    {
      icon: TrendingUp,
      iconColor: "text-[#F2673A]",
      title: "Pipeline Stage Analytics",
      desc: "Track velocity and bottlenecks",
    },
    {
      icon: Shield,
      iconColor: "text-[#366EB6]",
      title: "Burnout Risk Monitoring",
      desc: "Protect top talent from overwork",
    },
    {
      icon: LayoutDashboard,
      iconColor: "text-[#475569]",
      title: "Client Delivery Dashboards",
      desc: "Monitor quality and speed",
    },
    {
      icon: Activity,
      iconColor: "text-[#6C59A6]",
      title: "Activity Quality Insights",
      desc: "Distinguish productive work",
    },
    {
      icon: Users,
      iconColor: "text-[#F43F5E]",
      title: "Org-Level Benchmarking",
      desc: "Compare team performance",
    },
  ];

  // Outcomes per Figma
  const customOutcomes = [
    {
      percentage: "28%",
      title: "Faster Placements",
      desc: "Remove pipeline friction quickly. 28% faster time-to-fill.",
    },
    {
      percentage: "35%",
      title: "Higher Recruiter Productivity",
      desc: "Scale what actually works. 35% more placements per recruiter.",
    },
    {
      percentage: "40%",
      title: "Lower Recruiter Turnover",
      desc: "Protect top talent from burnout. 40% reduction in recruiter churn.",
    },
    {
      percentage: "32%",
      title: "Improved Client Satisfaction",
      desc: "Deliver faster, higher-quality results. 32% increase in client NPS.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How can TeamGrid help recruiters improve time-to-fill?", acceptedAnswer: { "@type": "Answer", text: "TeamGrid analyzes recruiter work patterns to identify effective behaviors that shorten time-to-fill and improve overall recruiter productivity." } },
            { "@type": "Question", name: "Does TeamGrid track recruiter activity or focus on outcomes?", acceptedAnswer: { "@type": "Answer", text: "TeamGrid focuses on outcomes and productivity trends rather than micromanaging recruiter activity, keystrokes, or individual actions." } },
            { "@type": "Question", name: "Can TeamGrid integrate with HR and ATS tools?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid works alongside existing HR systems and applicant tracking systems by enhancing staffing productivity insights without replacing core HR software." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="HR & Staffing Firms"
      heroTitle="Recruiter Performance & <span style='color: #00D1C1; font-weight: 600;'>Productivity Software for Staffing Teams</span>"
      heroSubtitle="Increase Placements Without Burning Out Recruiters"
      primaryButtonText="Get a Demo"
      customPrimaryButton={customButton}
      productivityTitle="Key Productivity Areas"
      productivitySubtitle="See how top staffing firms improve time-to-fill and recruiter performance sustainably."
      productivityCards={productivityCards}
      productivityCardAccentColors={productivityCardAccentColors}
      featureSuiteTitle="Complete Feature Suite"
      featureSuiteSubtitle="Everything you need in recruiter productivity software"
      customFeatures={customFeatures}
      customOutcomes={customOutcomes}
      outcomesTitle="Outcomes You Can Expect"
      whyChooseTitle="Why Staffing Firms Choose TeamGrid"
      whyChooseDescription="TeamGrid goes beyond recruiter activity tracking. It reveals how work patterns translate into placements helping firms grow without exhausting their teams."
      whyChooseButtonText="See TeamGrid In Action"
        faqs={hrStaffingFAQs}
        faqTitle="Frequently Asked Questions"
        faqSubtitle="Everything you need to know about TeamGrid for HR and staffing firms."
        faqBackgroundColor="#F3F4F6"
      />
    </>
  );
};

export default HRStaffingFirms;
